/**
 * <gymstack-tabs>
 * Shows its items as horizontal tabs on desktop (>= 769px) and as an
 * independent <details> accordion on mobile, from a single copy of the markup.
 */
if (!customElements.get('gymstack-tabs')) {
  class GymstackTabs extends HTMLElement {
    constructor() {
      super();
      this.tablist = this.querySelector('[role="tablist"]');
      this.tabs = Array.prototype.slice.call(this.querySelectorAll('[role="tab"]'));
      this.items = Array.prototype.slice.call(this.querySelectorAll('.gsi__item'));
      this.panels = Array.prototype.slice.call(this.querySelectorAll('.gsi__panel'));
      this.openFirst = this.hasAttribute('data-open-first');
      this.mql = window.matchMedia('(min-width: 769px)');
      this.activeIndex = 0;
    }

    connectedCallback() {
      if (this._init) return;
      this._init = true;

      if (this.tablist) {
        this.tablist.addEventListener('click', this.onTabClick.bind(this));
        this.tablist.addEventListener('keydown', this.onKeydown.bind(this));
      }

      this._onChange = this.applyMode.bind(this);
      if (this.mql.addEventListener) {
        this.mql.addEventListener('change', this._onChange);
      } else if (this.mql.addListener) {
        this.mql.addListener(this._onChange);
      }

      this.applyMode();
    }

    applyMode() {
      if (this.mql.matches) {
        this.enableTabs();
      } else {
        this.enableAccordion();
      }
    }

    enableTabs() {
      this.classList.add('is-tabs');
      this.classList.remove('is-accordion');
      // Force every panel to be available; visibility is controlled per-tab.
      this.items.forEach(function (item) { item.open = true; });
      this.selectTab(this.activeIndex, false);
    }

    selectTab(index, focus) {
      if (index < 0) index = 0;
      if (index > this.tabs.length - 1) index = this.tabs.length - 1;
      this.activeIndex = index;

      this.tabs.forEach(function (tab, i) {
        var selected = i === index;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        if (selected) {
          tab.removeAttribute('tabindex');
        } else {
          tab.setAttribute('tabindex', '-1');
        }
      });

      this.panels.forEach(function (panel, i) {
        panel.hidden = i !== index;
      });

      if (focus && this.tabs[index]) this.tabs[index].focus();
    }

    onTabClick(evt) {
      var tab = evt.target.closest('[role="tab"]');
      if (!tab) return;
      var index = this.tabs.indexOf(tab);
      if (index === -1) return;
      this.selectTab(index, false);
    }

    onKeydown(evt) {
      var index = this.tabs.indexOf(document.activeElement);
      if (index === -1) return;

      var next = null;
      switch (evt.key) {
        case 'ArrowRight':
        case 'ArrowDown':
          next = (index + 1) % this.tabs.length;
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          next = (index - 1 + this.tabs.length) % this.tabs.length;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = this.tabs.length - 1;
          break;
        default:
          return;
      }

      evt.preventDefault();
      this.selectTab(next, true);
    }

    enableAccordion() {
      this.classList.add('is-accordion');
      this.classList.remove('is-tabs');
      // Restore native accordion behaviour: panels visible, <details> controls open state.
      this.panels.forEach(function (panel) { panel.hidden = false; });
      var openFirst = this.openFirst;
      this.items.forEach(function (item, i) {
        item.open = openFirst ? i === 0 : false;
      });
    }
  }

  customElements.define('gymstack-tabs', GymstackTabs);
}
